import React from 'react';
import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  RefreshCw, 
  Package, 
  AlertCircle,
  CreditCard,
  Loader2
} from 'lucide-react';

export type StatusType = 
  | 'paid' 
  | 'pending' 
  | 'failed' 
  | 'refunded' 
  | 'refunded_partial' 
  | 'refunded_full'
  | 'in_progress' 
  | 'ready' 
  | 'todo'
  | 'cancelled'
  | 'disputed';

export interface StatusBadgeProps {
  status: StatusType;
  size?: 'sm' | 'md';
  showIcon?: boolean;
  className?: string;
  children?: React.ReactNode;
}

// Composant Dot pulsant pour les états "en attente"
const PulsingDot: React.FC<{ color: string; size: 'sm' | 'md' }> = ({ color, size }) => {
  const dotSize = size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2';
  return (
    <span className="relative flex">
      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${color}`}></span>
      <span className={`relative inline-flex rounded-full ${dotSize} ${color}`}></span>
    </span>
  );
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'md',
  showIcon = true,
  className = '',
  children,
}) => {
  const baseClasses = 'inline-flex items-center gap-1.5 font-bold rounded-full border transition-all';
  
  const statusConfig: Record<StatusType, {
    classes: string;
    icon: React.FC<{ className?: string }> | null;
    label: string;
    usePulsingDot?: boolean;
    dotColor?: string;
  }> = {
    paid: {
      classes: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
      icon: CheckCircle,
      label: 'Payé',
    },
    pending: {
      classes: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
      icon: null,
      label: 'En attente',
      usePulsingDot: true,
      dotColor: 'bg-amber-500',
    },
    failed: {
      classes: 'bg-red-500/15 text-red-600 border-red-500/30',
      icon: XCircle,
      label: 'Échoué',
    },
    refunded: {
      classes: 'bg-purple-500/15 text-purple-600 border-purple-500/30',
      icon: RefreshCw,
      label: 'Remboursé',
    },
    refunded_partial: {
      classes: 'bg-orange-500/15 text-orange-600 border-orange-500/30',
      icon: RefreshCw,
      label: 'Remb. partiel',
    },
    refunded_full: {
      classes: 'bg-purple-500/15 text-purple-600 border-purple-500/30',
      icon: RefreshCw,
      label: 'Remb. total',
    },
    in_progress: {
      classes: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
      icon: Loader2,
      label: 'En cours',
    },
    ready: {
      classes: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
      icon: CheckCircle,
      label: 'Prêt',
    },
    todo: {
      classes: 'bg-slate-500/15 text-slate-500 border-slate-500/30',
      icon: null,
      label: 'À faire',
      usePulsingDot: true,
      dotColor: 'bg-slate-400',
    },
    cancelled: {
      classes: 'bg-gray-500/15 text-gray-500 border-gray-500/30',
      icon: XCircle,
      label: 'Annulé',
    },
    disputed: {
      classes: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30',
      icon: AlertCircle,
      label: 'Litige',
    },
  };
  
  const config = statusConfig[status];
  const IconComponent = config.icon;
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-[10px]',
    md: 'px-2.5 py-1 text-xs',
  };
  
  const iconSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
  };
  
  const combinedClasses = `${baseClasses} ${config.classes} ${sizeClasses[size]} ${className}`;
  
  // Animation pour l'icône "in_progress"
  const iconClasses = `${iconSizeClasses[size]} ${status === 'in_progress' ? 'animate-spin' : ''}`;
  
  return (
    <span className={combinedClasses}>
      {showIcon && (
        config.usePulsingDot && config.dotColor ? (
          <PulsingDot color={config.dotColor} size={size} />
        ) : IconComponent ? (
          <IconComponent className={iconClasses} />
        ) : null
      )}
      <span>{children || config.label}</span>
    </span>
  );
};

export default StatusBadge;

